import * as React from "react";
import { $, merge } from "glamor";
import { formatMessage } from "lib/globalize-util";
import { RuleGroups } from "./rule-groups";
import Button from "components/form/button";
import Dialog from "components/dialog";
import { findDOMNode } from "react-dom";
import { CircularProgress } from "material-ui";
import { theme } from "styles/theme";
import { List } from "immutable";
import { DATATEST } from "constants/integration";
import type { IScope } from "reducers/scope";
import { AddHintBadge } from "components/hint-badge";
import CustomToggle from "components/form/switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import {
    addGroup,
    addRule,
    removeGroup,
    removeGroupRule,
    removeGroupRuleValue,
    removeTemporaryIds,
    selectGroupRuleField,
    setDefaultGroupNames,
    toggleGroupType,
    toggleRuleType,
    updateGroupField,
    checkForRules,
    GroupType,
    IGroupList,
} from "./group-utils";
import { KEYBOARD_KEYS } from "constants/keyboard";

interface IProps {
    scope: IScope;
    brandId: string;
    fields: any;
    groups: IGroupList;
    isUpdating: boolean;
    onSave: ({ excludeNew, groups }: { excludeNew: boolean; groups: IGroupList }) => void;
    // Optional props
    canAddGroup?: boolean;
    canEditGroupName?: boolean;
    canRemoveGroup?: boolean;
    canToggleGroupType?: boolean;
    canToggleExcludeGroup?: boolean;
    canToggleRuleType?: boolean;
    displayAllProducts?: boolean;
    filters?: any;
    filterValues?: any;
    onFetchFilterValues?: (field, search) => void;
    onResetFilterValues?: () => void;
    isSaving?: boolean;
    externalSaveButtons?: boolean;
    onSaveComplete?: () => void;
    isAddingGroup?: boolean;
    onAddGroupComplete?: () => void;
    onUpdateCanSave?: (canSave: boolean) => void;
}

interface IState {
    excludeNew: boolean;
    canSave: boolean;
    groups: any;
    isRemoveGroupDialogOpen: boolean;
    isRemoveRuleDialogOpen: boolean;
    isWhiteListNoRulesGroupDialogOpen: boolean;
    ruleToRemove: string;
    groupToRemove: string;
}

export class RuleGroupsManager extends React.PureComponent<IProps, IState> {
    createGroupButton: any;

    constructor(props) {
        super(props);
        const { scope } = props;
        this.state = {
            excludeNew: !!scope.brand.getPreference("exclude_new_groups"),
            canSave: false,
            groups: List(this.props.groups),
            groupToRemove: "",
            isRemoveGroupDialogOpen: false,
            isRemoveRuleDialogOpen: false,
            isWhiteListNoRulesGroupDialogOpen: false,
            ruleToRemove: "",
        };
    }

    styles = merge(
        {
            marginBottom: "48px",
            maxWidth: "900px",
        },
        $(" .hidden", {
            display: "none",
        }),
        $(" .buttonsContainer", {
            alignItems: "center",
            borderTop: `1px solid ${theme.palette.borderColor}`,
            display: "flex",
            justifyContent: "space-between",
            marginTop: "16px",
            paddingTop: "16px",
        }),
        $(" .rightButtons", {
            alignItems: "center",
            display: "flex",
        }),
        $(" .toggleRoot", {
            margin: "5px 0",
        }),
        $(" .toggleLabel", {
            fontSize: "14px",
            fontFamily: "inherit",
            color: `${theme.palette.textColor}`,
        })
    );
    muiStyles: any = {
        circularProgress: {
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            width: "94px",
        },
        actionsContainerStyle: {
            display: "flex",
            justifyContent: "space-around",
        },
        contentStyle: {
            maxWidth: "300px",
        },
        cancelButton: {
            marginRight: "12px",
        },
    };
    componentDidUpdate(prevProps, prevState) {
        if (prevProps.isUpdating && !this.props.isUpdating) {
            if (!this.props.groups.equals(prevProps.groups)) {
                this.setState({ groups: this.props.groups, canSave: false });
            } else {
                this.setState({ canSave: false });
            }
        }
        if (prevProps.displayAllProducts !== this.props.displayAllProducts) {
            this.setState({ canSave: true });
        }
        if (prevProps.isSaving !== this.props.isSaving) {
            if (this.props.isSaving) {
                this.handleCheckSave();
                this.props.onSaveComplete();
            }
        }
        if (prevProps.isAddingGroup !== this.props.isAddingGroup) {
            if (this.props.isAddingGroup) {
                this.handleAddGroup();
                this.props.onAddGroupComplete();
            }
        }
        if (prevState.canSave !== this.state.canSave) {
            this.props.onUpdateCanSave?.(this.state.canSave);
        }
    }
    bindRef = (el) => (this.createGroupButton = el);
    handleScrollToBottom = () => {
        const bottom = this.props.externalSaveButtons
            ? document.body.scrollHeight
            : (findDOMNode(this.createGroupButton) as HTMLElement).offsetTop;
        process.nextTick(() => window.scrollTo(0, bottom));
    };
    handleAddGroup = () => {
        this.setState(addGroup(this.state.excludeNew), this.handleScrollToBottom);
    };
    handleAddRule = (event) => {
        const groupId = event.currentTarget.getAttribute("data-addrule");
        this.setState(({ groups }) => addRule(groups, groupId, this.props.fields));
    };
    handleAddValue = (event, options?) => {
        // tslint:disable-next-line
        let groupId, ruleId, fieldValue;
        if (!event) {
            groupId = options.groupId;
            ruleId = options.ruleId;
            fieldValue = options.fieldValue;
        } else {
            [groupId, ruleId] = event.currentTarget.getAttribute("data-addvalue").split("-");
            fieldValue = event.currentTarget.value;
        }
        if (fieldValue && (!event || event.key === KEYBOARD_KEYS.ENTER || event.key === KEYBOARD_KEYS.TAB)) {
            // tslint:disable-next-line
            let groupIndex, ruleIndex;
            this.setState(({ groups }) => {
                groupIndex = groups.findIndex((group) => group.get("_id") === groupId);
                ruleIndex = groups
                    .getIn([groupIndex, "product_filter_values"])
                    .findIndex((value) => value.get("_id") === ruleId);
                if (groupIndex === -1 || ruleIndex === -1) {
                    return null;
                }
                return {
                    canSave: true,
                    groups: groups.updateIn([groupIndex, "product_filter_values", ruleIndex, "values"], (values) => {
                        return !values.includes(fieldValue) ? values.push(fieldValue) : values;
                    }),
                };
            });
        }
    };
    handleChangeGroupName = (groupId, value) => {
        this.setState(({ groups }) => updateGroupField(groups, groupId, "name", value));
    };
    handleCloseRemoveGroupDialog = () => {
        this.setState({ isRemoveGroupDialogOpen: false });
    };
    handleCloseRemoveRuleDialog = () => {
        this.setState({ isRemoveRuleDialogOpen: false });
    };
    handleCloseWhiteListNoRulesGroupDialog = () => {
        this.setState({ isWhiteListNoRulesGroupDialogOpen: false });
    };
    handleFetchOrAddFilterValues = (action, field, value, options?) => {
        if (action === "add") {
            this.handleAddValue(null, { ...options, fieldValue: value });
        } else {
            this.props.onFetchFilterValues(field, value);
        }
    };
    handleOpenRemoveGroupDialog = (event) => {
        this.setState({
            isRemoveGroupDialogOpen: true,
            groupToRemove: event.currentTarget.getAttribute("data-removegroup"),
        });
    };
    handleOpenRemoveRuleDialog = (event) => {
        this.setState({
            isRemoveRuleDialogOpen: true,
            ruleToRemove: event.currentTarget.getAttribute("data-removerule"),
        });
    };
    handleOpenWhiteListNoRulesGroupDialog = () => {
        this.setState({
            isWhiteListNoRulesGroupDialogOpen: true,
        });
    };
    handleRemoveGroup = (event) => {
        this.setState(removeGroup);
    };
    handleRemoveRule = (event) => {
        this.setState(removeGroupRule);
    };
    handleRemoveValue = (groupId, ruleId, fieldValue) => {
        this.setState(({ groups }) => removeGroupRuleValue(groups, groupId, ruleId, fieldValue));
    };
    handleSelectField = (event) => {
        const [groupId, ruleId, fieldKey] = event.currentTarget.getAttribute("data-selectrulefield").split("-");
        this.setState(({ groups }) => selectGroupRuleField(groups, groupId, ruleId, fieldKey));
    };

    handleToggleRuleType = (event) => {
        const [groupId, ruleId] = event.currentTarget.getAttribute("data-ruletoggle").split("-");
        this.setState(({ groups }) => toggleRuleType(groups, groupId, ruleId));
    };

    render() {
        const {
            brandId,
            canAddGroup,
            canEditGroupName,
            canRemoveGroup,
            canToggleGroupType,
            canToggleExcludeGroup,
            canToggleRuleType,
            displayAllProducts,
            fields,
            filters,
            filterValues,
            isUpdating,
            onFetchFilterValues,
            onResetFilterValues,
            scope,
            externalSaveButtons,
        } = this.props;
        const {
            canSave,
            groups,
            isRemoveGroupDialogOpen,
            isRemoveRuleDialogOpen,
            isWhiteListNoRulesGroupDialogOpen,
            excludeNew,
        } = this.state;
        const useCatalog = scope.brand.hasPreference("linesheets_are_catalogs");
        return (
            <div {...this.styles}>
                {canToggleExcludeGroup && (
                    <div className="excludeContainer">
                        <AddHintBadge
                            hint='Enable to set all new customer groups created to "exclude this group from all linesheets" by default. Best for brands that create customer-specific linesheets.'
                            variables={{
                                linesheets: useCatalog ? formatMessage("catalogs") : formatMessage("linesheets"),
                            }}
                        >
                            <FormControlLabel
                                classes={{ root: "toggleRoot", label: "toggleLabel" }}
                                control={
                                    <CustomToggle
                                        inputProps={
                                            {
                                                "data-test": DATATEST.CUSTOMER_USER_GROUPS.EXCLUDE_NEW_GROUPS_TOGGLE,
                                            } as any
                                        }
                                        checked={excludeNew}
                                        onChange={this.handleExcludeNewChange}
                                    />
                                }
                                label={formatMessage("Exclude all groups from all Linesheets by default", {
                                    Linesheets: useCatalog ? formatMessage("Catalogs") : formatMessage("Linesheets"),
                                })}
                            />
                        </AddHintBadge>
                    </div>
                )}
                <RuleGroups
                    scope={scope}
                    canEditGroupName={canEditGroupName}
                    displayAllProducts={displayAllProducts}
                    fields={fields}
                    groups={groups}
                    onAddRule={this.handleAddRule}
                    onAddValue={this.handleAddValue}
                    onChangeGroupName={this.handleChangeGroupName}
                    onChangeExclude={canToggleExcludeGroup && this.handleChangeExclude}
                    onRemoveRule={this.handleOpenRemoveRuleDialog}
                    onRemoveValue={this.handleRemoveValue}
                    onSelectField={this.handleSelectField}
                    /* Online Trade Show Setup Container related props */
                    filters={filters}
                    filterValues={filterValues}
                    onFetchFilterValues={onFetchFilterValues && this.handleFetchOrAddFilterValues}
                    onResetFilterValues={onResetFilterValues}
                    onToggleRuleType={canToggleRuleType && this.handleToggleRuleType}
                    /* Manage Customer and User Groups Container related props */
                    onRemoveGroup={canRemoveGroup && this.handleOpenRemoveGroupDialog}
                    onToggleGroupType={canToggleGroupType && this.handleToggleGroupType}
                />
                {!externalSaveButtons && (
                    <div className="buttonsContainer">
                        <div className="leftButtons">
                            {canAddGroup && (
                                <Button
                                    data-creategroup="create-group"
                                    label={formatMessage("Create New Group")}
                                    data-test={DATATEST.CUSTOMER_USER_GROUPS.ADD_CUSTOMER_GROUP_BTN}
                                    onClick={this.handleAddGroup}
                                    ref={this.bindRef}
                                />
                            )}
                        </div>
                        <div className="rightButtons">
                            <Button
                                label={formatMessage("Back")}
                                href={`/brand/${brandId}/admin/brand?activeTab=settings`}
                                style={this.muiStyles.cancelButton}
                            />
                            <Button
                                disabled={!canSave}
                                primary={true}
                                data-test={DATATEST.CUSTOMER_USER_GROUPS.SAVE_BTN}
                                onClick={this.handleCheckSave}
                                {...(!isUpdating && { label: formatMessage("Save") })}
                            >
                                {isUpdating && (
                                    <CircularProgress
                                        color="white"
                                        data-loading="loading"
                                        size={30}
                                        thickness={3}
                                        style={this.muiStyles.circularProgress}
                                    />
                                )}
                            </Button>
                        </div>
                    </div>
                )}
                {canRemoveGroup && isRemoveGroupDialogOpen && (
                    <Dialog
                        actions={[
                            <Button
                                key="yes"
                                label={formatMessage("Yes")}
                                data-test={DATATEST.CUSTOMER_USER_GROUPS.CONFIRM_REMOVE_GROUP_BTN}
                                onClick={this.handleRemoveGroup}
                                primary={true}
                            />,
                            <Button
                                key="cancel"
                                label={formatMessage("Cancel")}
                                onClick={this.handleCloseRemoveGroupDialog}
                                data-test={DATATEST.CUSTOMER_USER_GROUPS.CANCEL_REMOVE_GROUP_BTN}
                            />,
                        ]}
                        actionsContainerStyle={this.muiStyles.actionsContainerStyle}
                        contentStyle={this.muiStyles.contentStyle}
                        data-dialog="remove-group"
                        onRequestClose={this.handleCloseRemoveGroupDialog}
                        open={true}
                        title={formatMessage("Remove Group")}
                    >
                        <p>{formatMessage("Remove this group and all of its rules?")}</p>
                    </Dialog>
                )}
                {isRemoveRuleDialogOpen && (
                    <Dialog
                        actions={[
                            <Button
                                key="yes"
                                primary={true}
                                label={formatMessage("Yes")}
                                data-test={DATATEST.CUSTOMER_USER_GROUPS.CONFIRM_REMOVE_RULE_BTN}
                                onClick={this.handleRemoveRule}
                            />,
                            <Button
                                key="cancel"
                                label={formatMessage("Cancel")}
                                onClick={this.handleCloseRemoveRuleDialog}
                            />,
                        ]}
                        actionsContainerStyle={this.muiStyles.actionsContainerStyle}
                        contentStyle={this.muiStyles.contentStyle}
                        data-dialog="remove-rule"
                        onRequestClose={this.handleCloseRemoveRuleDialog}
                        open={true}
                        title={formatMessage("Remove Rule")}
                    >
                        <p>{formatMessage("Remove this rule and all of its values?")}</p>
                    </Dialog>
                )}

                {isWhiteListNoRulesGroupDialogOpen && (
                    <Dialog
                        actions={[
                            <Button
                                data-test={DATATEST.CUSTOMER_USER_GROUPS.DIALOG_CONFIRM}
                                key="yes"
                                primary={true}
                                label={formatMessage("Yes")}
                                onClick={this.handleSaveWhiteListNoRules}
                            />,
                            <Button
                                data-test={DATATEST.CUSTOMER_USER_GROUPS.DIALOG_CANCEL}
                                key="cancel"
                                label={formatMessage("Cancel")}
                                onClick={this.handleCloseWhiteListNoRulesGroupDialog}
                            />,
                        ]}
                        actionsContainerStyle={this.muiStyles.actionsContainerStyle}
                        contentStyle={this.muiStyles.contentStyle}
                        data-dialog="whitelist-no-rule"
                        onRequestClose={this.handleCloseWhiteListNoRulesGroupDialog}
                        open={true}
                        title={formatMessage("Are you sure?")}
                    >
                        <p data-test={DATATEST.CUSTOMER_USER_GROUPS.ERROR_MESSAGE_DIALOG}>
                            {formatMessage(
                                "No products will be visible to members of this group as you have created a whitelist group with no rules. Are you sure you want to proceed?"
                            )}
                        </p>
                    </Dialog>
                )}
            </div>
        );
    }

    private handleSaveWhiteListNoRules = () => {
        this.handleCloseWhiteListNoRulesGroupDialog();
        this.handleSave();
    };
    private handleCheckSave = () => {
        const temporaryWhiteListGroups = this.state.groups.filter(
            (group) => group.get("_id").startsWith("_") && group.get("type") === GroupType.WhiteList
        );
        if (temporaryWhiteListGroups.size === checkForRules(temporaryWhiteListGroups)) {
            this.handleSave();
        } else {
            this.handleOpenWhiteListNoRulesGroupDialog();
        }
    };
    private handleSave = () => {
        if (this.state.canSave) {
            this.props.onSave({
                excludeNew: this.state.excludeNew,
                groups: setDefaultGroupNames(removeTemporaryIds(this.state.groups)),
            });
        }
    };

    private handleToggleGroupType = (groupId: string) => {
        this.setState(({ groups }) => toggleGroupType(groups, groupId));
    };

    private handleChangeExclude = (groupId: string, exclude: boolean) => {
        this.setState(({ groups }) => updateGroupField(groups, groupId, "exclude", exclude));
    };

    private handleExcludeNewChange = () => {
        this.setState({ canSave: true, excludeNew: !this.state.excludeNew });
    };
}

export default RuleGroupsManager;
